import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FINANCING_KINDS, FINANCING_KIND_LABELS, FINANCING_STATUSES, FINANCING_STATUS_LABELS, formatCents, type CreateFinancingInput, type Financing, type FinancingDetail, type FinancingOverview, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { SortTh } from '../../components/SortTh';
import { useSession } from '../../session';
import { FinancingForm } from './FinancingForm';
import { FinancingTabs } from './FinancingTabs';
import { errorText, formatDay, monthText } from './shared';
import '../fleet/fleet.css';
import './financing.css';

/**
 * Finanzierungen und Leasings (KON-253): Kennzahlen (Zahlungen im Monat und Jahr, Restschuld, Restverpflichtung), Vorschau der nächsten zwölf Monate und die Liste aller Verträge. Die Zahlen
 * fließen in den Finanzreport und in die Auswertungen ein.
 */
export function FinancingPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const kind = params.get('kind') ?? '';
  const status = params.get('status') ?? '';
  const sort = params.get('sort') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const overview = useQuery({ queryKey: ['financing-overview'], queryFn: async () => (await api<{ data: FinancingOverview }>('GET', '/financing/overview')).data });
  const list = useQuery({
    queryKey: ['financing-list', { q, kind, status, sort, page }],
    queryFn: () => api<Paginated<Financing>>('GET', `/financing${queryString({ q, kind, status, sort, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const fields: FilterField[] = [
    { key: 'kind', label: 'Art', type: 'select', options: [{ value: '', label: 'Alle' }, ...FINANCING_KINDS.map((k) => ({ value: k, label: FINANCING_KIND_LABELS[k] }))] },
    { key: 'status', label: 'Stand', type: 'select', options: [{ value: '', label: 'Alle' }, ...FINANCING_STATUSES.map((s) => ({ value: s, label: FINANCING_STATUS_LABELS[s] }))] },
  ];
  const create = useMutation({
    mutationFn: async (body: CreateFinancingInput) => (await api<{ data: FinancingDetail }>('POST', '/financing', body)).data,
    onSuccess: (created) => {
      for (const key of ['financing-list', 'financing-overview']) client.invalidateQueries({ queryKey: [key] });
      navigate(`/finanzierungen/${created.id}`);
    },
    onError: (e) => setError(errorText(e, 'Der Vertrag konnte nicht angelegt werden.')),
  });

  const o = overview.data;
  const maxPayment = Math.max(1, ...(o?.forecast.map((m) => m.paymentCents) ?? [1]));

  return (
    <>
      <FinancingTabs />
      <div className="page-head">
        <h1>Finanzierungen und Leasings</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} Verträge` : ''}</span>
        {can('financing.write') && <button type="button" className="btn btn--primary" onClick={() => { setError(null); setCreating(!creating); }}>Neuer Vertrag</button>}
      </div>
      <p className="muted">Laufende Darlehen, Ratenkäufe und Leasingverträge mit Zahlungsplan. Leasingraten, Zinsen und Gebühren zählen im Finanzreport und in den Auswertungen als Aufwand, die Tilgung nicht.</p>

      {o && (
        <div className="fleet-tiles">
          <div className="card fleet-tile"><span className="muted small">Laufende Verträge</span><strong>{o.activeCount}</strong></div>
          <div className="card fleet-tile"><span className="muted small">Zahlungen diesen Monat</span><strong>{formatCents(o.monthCents)}</strong><span className="muted small">brutto, Raten und Gebühren</span></div>
          <div className="card fleet-tile"><span className="muted small">Zahlungen dieses Jahr</span><strong>{formatCents(o.yearCents)}</strong></div>
          <div className="card fleet-tile"><span className="muted small">Restschuld Darlehen</span><strong>{formatCents(o.loanDebtCents)}</strong><span className="muted small">Darlehen und Ratenkäufe</span></div>
          <div className="card fleet-tile"><span className="muted small">Leasingverpflichtung</span><strong>{formatCents(o.leaseCommitmentCents)}</strong><span className="muted small">Raten bis zum Vertragsende, brutto</span></div>
          {o.overdueCount > 0 && <div className="card fleet-tile"><span className="muted small">Überfällige Raten</span><strong className="fleet-warn">{o.overdueCount}</strong><span className="muted small">nicht als bezahlt vermerkt</span></div>}
        </div>
      )}

      {o && o.upcoming.length > 0 && (
        <section className="card" aria-labelledby="fin-next">
          <h2 id="fin-next">Nächste Zahlungen</h2>
          <ul className="inv-list">
            {o.upcoming.map((u, i) => (
              <li key={`${u.financingId}-${u.dueDate}-${i}`}>{formatDay(u.dueDate)} · <Link to={`/finanzierungen/${u.financingId}`}>{u.title}</Link> · {formatCents(u.paymentCents)}</li>
            ))}
          </ul>
        </section>
      )}

      {o && o.activeCount > 0 && (
        <CollapsibleCard title="Vorschau der nächsten zwölf Monate" storageKey="financing-forecast" defaultOpen>
          <div className="table-card">
            <table className="table table--cards fin-forecast">
              <thead><tr><th>Monat</th><th className="num">Zahlungen</th><th /><th className="num">Zinsen</th><th className="num">Tilgung</th><th className="num">Leasing und Gebühren</th><th className="num">Aufwand netto</th><th className="num">Restschuld am Monatsende</th></tr></thead>
              <tbody>
                {o.forecast.map((m) => (
                  <tr key={m.month}>
                    <td data-label="Monat">{monthText(m.month)}</td>
                    <td data-label="Zahlungen" className="num"><strong>{formatCents(m.paymentCents)}</strong></td>
                    <td className="cell-bar" aria-hidden="true"><span className="fin-forecast-bar" style={{ width: `${Math.round((m.paymentCents / maxPayment) * 100)}%` }} /></td>
                    <td data-label="Zinsen" className="num">{formatCents(m.interestCents)}</td>
                    <td data-label="Tilgung" className="num">{formatCents(m.principalCents)}</td>
                    <td data-label="Leasing und Gebühren" className="num">{formatCents(m.leaseCents)}</td>
                    <td data-label="Aufwand netto" className="num">{formatCents(m.expenseNetCents)}</td>
                    <td data-label="Restschuld am Monatsende" className="num">{formatCents(m.debtEndCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small">Die Vorschau zählt alle Raten laufender Verträge nach Fälligkeit. Der Aufwand netto ist der Teil, der im Finanzreport als Kosten erscheint.</p>
        </CollapsibleCard>
      )}

      {creating && <FinancingForm onSubmit={(b) => { setError(null); create.mutate(b); }} onCancel={() => setCreating(false)} pending={create.isPending} error={error} />}

      <FilterBar search={{ value: q, placeholder: 'Bezeichnung, Geldgeber, Nummer oder Vertragsnummer', onSubmit: (v) => update({ q: v }) }} fields={fields} values={{ kind, status }} onChange={(k, v) => update({ [k]: v })} onReset={() => setParams(new URLSearchParams())} />
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <SortTh label="Vertrag" sortKey="title" sort={sort} onSort={(s) => update({ sort: s })} />
              <th>Art</th>
              <SortTh label="Geldgeber" sortKey="lender" sort={sort} onSort={(s) => update({ sort: s })} />
              <th>Gegenstand</th>
              <th className="num">Rate</th>
              <th className="num">Restschuld</th>
              <SortTh label="Beginn" sortKey="startDate" sort={sort} onSort={(s) => update({ sort: s })} first="desc" />
              <th>Ende</th>
              <th>Stand</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={10} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && <tr><td colSpan={10} className="empty">Noch keine Verträge. Legen Sie mit „Neuer Vertrag“ Ihr erstes Darlehen oder Leasing an.</td></tr>}
            {list.data?.data.map((f) => (
              <ExpandRow key={f.id} summary={<Summary title={<Link to={`/finanzierungen/${f.id}`} className="strong-link">{f.title}</Link>} aside={<strong>{formatCents(f.installmentCents)}</strong>} line2={`${f.number} · ${FINANCING_KIND_LABELS[f.kind]} · ${f.lenderName}`} line3={f.summary.overdueCount > 0 ? `${f.summary.overdueCount} Raten überfällig` : `Restschuld ${formatCents(f.summary.debtCents)}`} />}>
                <td className="cell-desktop-title"><Link to={`/finanzierungen/${f.id}`} className="strong-link">{f.title}</Link><div className="muted small">{f.number}</div></td>
                <td data-label="Art">{FINANCING_KIND_LABELS[f.kind]}</td>
                <td data-label="Geldgeber">{f.lenderName}</td>
                <td data-label="Gegenstand">{f.vehicleId ? <Link to={`/flotte/${f.vehicleId}`}>{f.vehiclePlate}</Link> : (f.objectDescription ?? '–')}</td>
                <td data-label="Rate" className="num">{formatCents(f.installmentCents)}</td>
                <td data-label="Restschuld" className="num">{formatCents(f.summary.debtCents)}</td>
                <td data-label="Beginn">{formatDay(f.startDate)}</td>
                <td data-label="Ende">{formatDay(f.summary.endDate)}</td>
                <td data-label="Stand">{f.summary.overdueCount > 0 ? <span className="fleet-warn">{f.summary.overdueCount} überfällig</span> : FINANCING_STATUS_LABELS[f.status]}</td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
